import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { db } from '../../../firebase-client';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { parse, startOfWeek, format, getDay } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../../components/teams/teams.css';
import EventFormModal from '../../../components/forms/teamForms/eventFormModal';
import { useAuth } from '../../../auth/AuthContext';
import { getRecurrenceDatesForDay, getWeeklyRecurrenceScanLimit } from '../../../utils/recurrence';
import { listVisibleTeamEvents } from '../../../utils/boardApi';
import { shouldEnableTeamCalendarSelection } from './calendarSelection';
import DateJumpInput from '../../../components/calendar/DateJumpInput';
import { reportCaughtError } from '../../../utils/clientErrorReporting';
import useStripePaymentGuard from '../../../components/payments/useStripePaymentGuard';
import { buildEventCopyInitialData } from '../../../components/forms/teamForms/events/eventCopy';
const locales = {
  'en-GB': enGB
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), {
    weekStartsOn: 1
  }),
  getDay,
  locales
});
function toOccurrenceId(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function formatDateInputValue(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function buildScrollToTime(dateLike) {
  const fallback = new Date();
  fallback.setHours(8, 0, 0, 0);
  if (!dateLike) return fallback;
  const candidate = new Date(dateLike);
  if (Number.isNaN(candidate.getTime())) return fallback;
  candidate.setSeconds(0, 0);
  return candidate;
}
function parseOccurrenceId(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}
function splitCompoundId(value) {
  if (typeof value !== 'string' || !value.includes('__')) {
    return {
      baseId: value || '',
      occurrenceId: null
    };
  }
  const parts = value.split('__').filter(Boolean);
  if (!parts.length) return {
    baseId: value,
    occurrenceId: null
  };
  const occurrenceId = parts.find(part => parseOccurrenceId(part)) || null;
  if (!occurrenceId) {
    return {
      baseId: value,
      occurrenceId: null
    };
  }
  return {
    baseId: parts.slice(0, parts.indexOf(occurrenceId)).join('__') || value,
    occurrenceId
  };
}
function getMirrorBaseEventId(data, docId) {
  if (data.originalEventId) return splitCompoundId(data.originalEventId).baseId || data.originalEventId;
  if (data.scope === 'occurrence' || data.occurrenceId) {
    const key = data.eventId || docId;
    return splitCompoundId(key).baseId || key;
  }
  const key = data.eventId || docId;
  return splitCompoundId(key).baseId || key;
}
function getMirrorOccurrenceId(data, docId) {
  if (parseOccurrenceId(data.occurrenceId)) return data.occurrenceId;
  if (parseOccurrenceId(data.date)) return data.date;
  if (data.scope === 'occurrence') {
    const key = data.eventId || docId;
    const parsed = splitCompoundId(key).occurrenceId;
    if (parsed) return parsed;
  }
  return splitCompoundId(docId).occurrenceId;
}
function getEventBaseId(data, docId) {
  const explicit = typeof data.originalEventId === 'string' ? data.originalEventId.trim() : '';
  if (explicit) return splitCompoundId(explicit).baseId || explicit;
  return splitCompoundId(docId).baseId || docId;
}
function getEventOccurrenceId(data, docId) {
  const compoundOccurrenceId = splitCompoundId(typeof data.eventId === 'string' ? data.eventId : '').occurrenceId || splitCompoundId(docId).occurrenceId || null;
  if (compoundOccurrenceId) return compoundOccurrenceId;
  if (data.scope === 'occurrence' || typeof data.originalEventId === 'string') {
    return parseOccurrenceId(data.occurrenceId) || parseOccurrenceId(data.date) || null;
  }
  return parseOccurrenceId(data.occurrenceId) || null;
}
function isOccurrenceOverrideDoc(data, docId) {
  const hasOccurrence = !!getEventOccurrenceId(data, docId);
  if (!hasOccurrence) return false;
  if (data.scope === 'occurrence') return true;
  if (typeof data.originalEventId === 'string' && data.originalEventId.trim() !== '') return true;
  if (typeof data.occurrenceId === 'string' && data.occurrenceId.trim() !== '') return true;
  return docId.includes('__');
}
function toStartForEvent(data, occurrenceId) {
  if (data.date && data.time) {
    const d = new Date(`${data.date}T${data.time}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (data.start?.toDate) {
    const d = data.start.toDate();
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (data.start) {
    const d = new Date(data.start);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (occurrenceId) {
    const inferredTime = (typeof data.time === 'string' && data.time.includes(':') ? data.time : null) || (typeof data.recurrence?.time === 'string' && data.recurrence.time.includes(':') ? data.recurrence.time : null) || '00:00';
    const d = new Date(`${occurrenceId}T${inferredTime}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}
export default function TeamCalendar() {
  const {
    state,
    search
  } = useLocation();
  const params = useParams();
  const {
    user,
    authLoading
  } = useAuth();
  const paramTeamId = params.teamId || params.teamid;
  const derivedTeamId = state?.teamId || paramTeamId;
  const teamId = derivedTeamId;
  const userId = user?.uid || '';
  const [isAdmin, setIsAdmin] = useState(false);
  const [canModerateEventComments, setCanModerateEventComments] = useState(false);
  const [canManageEventRefunds, setCanManageEventRefunds] = useState(false);
  const [canCheckIn, setCanCheckIn] = useState(false);
  const [authzChecked, setAuthzChecked] = useState(false);
  const eventQueryId = new URLSearchParams(search).get('event');
  const eventQueryOccurrence = new URLSearchParams(search).get('occurrence');
  const eventQueryComments = new URLSearchParams(search).get('comments') === '1';
  const [pendingQueryEvent, setPendingQueryEvent] = useState(eventQueryId || null);
  const [pendingQueryOccurrence, setPendingQueryOccurrence] = useState(eventQueryOccurrence || null);
  const [pendingQueryComments, setPendingQueryComments] = useState(eventQueryComments);
  const [teamName, setTeamName] = useState('Team');
  const [teamBrand, setTeamBrand] = useState({});
  const [rsvpSeriesResponsesEnabled, setRsvpSeriesResponsesEnabled] = useState(true);

  // Team events rendered on the calendar (expanded recurrences)
  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState('');

  // Live map of user mirror: users/{uid}/events
  // Shape:
  // { [eventId]: { series: { rsvpStatus, needsResponse } | null, occurrences: { [occurrenceId]: { rsvpStatus, needsResponse } } } }
  const [userEventMap, setUserEventMap] = useState({});
  const [userTicketMap, setUserTicketMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');
  const [scrollToTime, setScrollToTime] = useState(() => buildScrollToTime(null));
  const [calendarHeight, setCalendarHeight] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768 ? 420 : 560);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const calendarShellRef = useRef(null);
  const navigate = useNavigate();
  const { requireStripePayment, stripeGuardDialog } = useStripePaymentGuard(teamId);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    setPendingQueryEvent(eventQueryId || null);
    setPendingQueryOccurrence(eventQueryOccurrence || null);
    setPendingQueryComments(eventQueryComments);
  }, [eventQueryComments, eventQueryId, eventQueryOccurrence]);
  const openPaidEventEditor = useCallback(() => {
    setSelectedEvent({
      name: '',
      type: 'oneOff',
      date: '',
      time: '',
      durationMinutes: 60,
      eventMode: 'inPerson',
      onlineUrl: '',
      attendanceMode: 'ticketed',
      admissionTicketsEnabled: true,
      admissionTicketingLocked: false,
      allowNonMembers: false,
      ticketName: 'General Admission',
      ticketCurrency: 'GBP',
      ticketPricePence: '',
      ticketSaleStart: '',
      ticketSaleEnd: '',
      ticketPlatformFeeHandling: 'add',
      readOnly: false
    });
    setModalOpen(true);
  }, []);
  const openPaidEventModal = useCallback(() => {
    requireStripePayment({
      feature: 'paid_event',
      featureLabel: 'Paid events',
      returnTarget: 'paid_event',
      onReady: openPaidEventEditor,
    });
  }, [openPaidEventEditor, requireStripePayment]);
  useEffect(() => {
    const query = new URLSearchParams(search);
    if (!authzChecked || !isAdmin || query.get('resumeStripeAction') !== 'paid_event') return;
    query.delete('resumeStripeAction');
    navigate({ search: query.toString() ? `?${query.toString()}` : '' }, { replace: true });
    openPaidEventModal();
  }, [authzChecked, isAdmin, navigate, openPaidEventModal, search]);
  const openFreeEventModal = (slotStart = new Date()) => {
    const start = new Date(slotStart);
    if (Number.isNaN(start.getTime())) return;
    setSelectedEvent({
      name: '',
      type: 'oneOff',
      date: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
      time: start.toTimeString().slice(0, 5),
      durationMinutes: 60,
      eventMode: 'inPerson',
      onlineUrl: '',
      attendanceMode: 'rsvp',
      allowNonMembers: false,
      readOnly: false
    });
    setModalOpen(true);
  };
  const compactActionStyle = isMobile ? {
    padding: '0.45rem 0.7rem',
    fontSize: '0.9rem',
    lineHeight: 1.2
  } : undefined;
  const recalcCalendarHeight = useCallback(() => {
    if (typeof window === 'undefined') return;
    const top = calendarShellRef.current?.getBoundingClientRect().top ?? 0;
    const available = Math.floor(window.innerHeight - top - 12);
    const minHeight = isMobile ? 260 : 280;
    const maxHeight = Math.floor(window.innerHeight * (isMobile ? 0.86 : 0.85));
    let nextHeight = Math.min(available, maxHeight);
    if (available >= minHeight) {
      nextHeight = Math.max(nextHeight, minHeight);
    }
    setCalendarHeight(nextHeight);
  }, [isMobile]);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const raf = window.requestAnimationFrame(recalcCalendarHeight);
    window.addEventListener('resize', recalcCalendarHeight);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', recalcCalendarHeight);
    };
  }, [recalcCalendarHeight, isAdmin, calendarView]);

  // Validate the active account against the current club route.
  useEffect(() => {
    let didCancel = false;
    if (authLoading || !teamId || !userId) {
      setAuthzChecked(false);
      setIsAdmin(false);
      setCanModerateEventComments(false);
      setCanManageEventRefunds(false);
      setCanCheckIn(false);
      return () => {
        didCancel = true;
      };
    }
    setAuthzChecked(false);
    const verifyAccess = async () => {
      try {
        const teamRef = doc(db, 'teams', teamId);
        const [teamSnap, memberSnap, assignmentSnap] = await Promise.all([
          getDoc(teamRef),
          getDoc(doc(db, 'teams', teamId, 'members', userId)),
          getDoc(doc(db, 'teams', teamId, 'adminAssignments', userId)).catch(() => null),
        ]);
        if (didCancel) return;
        if (!teamSnap.exists()) {
          navigate('/teams', {
            replace: true
          });
          return;
        }
        const teamData = teamSnap.data() || {};
        const fullAdmin = Array.isArray(teamData.admins) && teamData.admins.includes(userId);
        const assignedKeys = assignmentSnap?.exists() && Array.isArray(assignmentSnap.data()?.permissionKeys)
          ? assignmentSnap.data().permissionKeys
          : [];
        const nextIsAdmin = fullAdmin || assignedKeys.includes('events.manage');
        const nextCanModerateEventComments = fullAdmin || assignedKeys.includes('communications.manage');
        const nextCanManageEventRefunds = fullAdmin || assignedKeys.includes('finance.manage');
        const nextCanCheckIn = nextIsAdmin || assignedKeys.includes('eventCheckIn.manage');
        setTeamName(teamData.name || 'Team');
        setTeamBrand(teamData.brand || {});
        setRsvpSeriesResponsesEnabled(teamData.eventSettings?.rsvpSeriesResponsesEnabled !== false);
        setIsAdmin(nextIsAdmin);
        setCanModerateEventComments(nextCanModerateEventComments);
        setCanManageEventRefunds(nextCanManageEventRefunds);
        setCanCheckIn(nextCanCheckIn);
        if (nextIsAdmin || nextCanManageEventRefunds || nextCanCheckIn || memberSnap.exists()) {
          setAuthzChecked(true);
          return;
        }
        navigate('/teams', {
          replace: true
        });
      } catch (err) {
        if (!didCancel) {
          setIsAdmin(false);
          setCanModerateEventComments(false);
          setCanCheckIn(false);
          navigate('/teams', {
            replace: true
          });
        }
      }
    };
    verifyAccess();
    return () => {
      didCancel = true;
    };
  }, [authLoading, navigate, teamId, userId]);

  // Live subscribe to the user's mirror events
  useEffect(() => {
    if (!authzChecked || !userId) return;
    const unsub = safeOnSnapshot(collection(db, 'users', userId, 'events'), snap => {
      const map = {};
      snap.forEach(d => {
        const data = d.data() || {};
        const baseEventId = getMirrorBaseEventId(data, d.id);
        if (!baseEventId) return;
        map[baseEventId] = map[baseEventId] || {
          series: null,
          occurrences: {}
        };
        const entry = {
          rsvpStatus: data.rsvpStatus ?? null,
          needsResponse: !!data.needsResponse
        };
        const scope = data.scope === 'occurrence' || data.occurrenceId ? 'occurrence' : 'series';
        if (scope === 'occurrence') {
          const occurrenceId = getMirrorOccurrenceId(data, d.id);
          if (occurrenceId) {
            map[baseEventId].occurrences[occurrenceId] = entry;
          }
        } else {
          map[baseEventId].series = entry;
        }
      });
      setUserEventMap(map);
    }, err => {
      reportCaughtError(err, {
        source: 'calendar.rsvp_mirror.read',
        severity: 'error',
        context: { teamId },
      });
    });
    return () => unsub && unsub();
  }, [authzChecked, teamId, userId]);

  // Paid ticket mirrors are server-owned in the private calendar. Keep a
  // separate scoped map so a series pass colours every covered occurrence,
  // while a per-occurrence ticket colours only its purchased date.
  useEffect(() => {
    if (!authzChecked || !userId || !teamId) return undefined;
    const unsub = safeOnSnapshot(collection(db, 'privateUsers', userId, 'calendar'), snap => {
      const map = {};
      snap.forEach(docSnap => {
        const data = docSnap.data() || {};
        if (data.teamId !== teamId || data.attendanceMode !== 'ticketed' || data.ticketStatus !== 'paid') return;
        const baseEventId = getMirrorBaseEventId(data, docSnap.id);
        if (!baseEventId) return;
        map[baseEventId] = map[baseEventId] || { series: false, occurrences: {} };
        const occurrenceId = getMirrorOccurrenceId(data, docSnap.id);
        if (data.scope === 'occurrence' || occurrenceId) {
          if (occurrenceId) map[baseEventId].occurrences[occurrenceId] = true;
        } else {
          map[baseEventId].series = true;
        }
      });
      setUserTicketMap(map);
    }, () => setUserTicketMap({}));
    return () => unsub && unsub();
  }, [authzChecked, teamId, userId]);

  // Load team events (one-offs + next 6 occurrences of recurring)
  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      if (!authzChecked || !teamId) return;
      let eventDocs = [];
      if (isAdmin) {
        eventDocs = (await getDocs(collection(db, 'teams', teamId, 'events'))).docs;
      } else {
        const result = await listVisibleTeamEvents({ teamId });
        eventDocs = (Array.isArray(result?.events) ? result.events : []).map(eventData => ({
          id: eventData.id,
          data: () => eventData
        }));
      }
      if (cancelled) return;
      const results = [];
      const occurrenceOverrides = new Map(); // `${seriesId}__${occurrenceId}` -> event object

      eventDocs.forEach(docSnap => {
        const data = docSnap.data() || {};
        const duration = Number(data.durationMinutes || 60) * 60 * 1000;
        const mode = data.mode || data.eventMode || 'inPerson';
        const onlineUrl = data.onlineUrl || '';
        const title = data.name || data.title || 'Untitled';
        const docId = docSnap.id;
        const parsedOccurrenceId = getEventOccurrenceId(data, docId);
        const parsedSeriesId = getEventBaseId(data, docId);
        const isOccurrenceOverride = !!parsedSeriesId && isOccurrenceOverrideDoc(data, docId);
        if (isOccurrenceOverride) {
          const start = toStartForEvent(data, parsedOccurrenceId);
          if (!start || Number.isNaN(start.getTime())) return;
          const end = data.end?.toDate ? data.end.toDate() : new Date(start.getTime() + duration);
          const {
            start: _s,
            end: _e,
            ...safe
          } = data;
          const key = `${parsedSeriesId}__${parsedOccurrenceId}`;
          occurrenceOverrides.set(key, {
            ...safe,
            id: key,
            originalId: parsedSeriesId,
            originalEventId: parsedSeriesId,
            occurrenceId: parsedOccurrenceId,
            title,
            start,
            end,
            type: 'oneOff',
            mode,
            onlineUrl,
            location: data.location || {},
            isOccurrenceOverride: true
          });
          return;
        }
        const scheduleType = (data.type || '').toLowerCase();
        const isOneOff = scheduleType === 'oneoff' || (!!data.date && !!data.time);
        const isRecurring = scheduleType === 'recurring';
        if (isOneOff && data.date && data.time) {
          const docStart = data.start?.toDate ? data.start.toDate() : null;
          const start = docStart || new Date(`${data.date}T${data.time}`);
          if (isNaN(start)) return;
          const end = data.end?.toDate ? data.end.toDate() : new Date(start.getTime() + duration);
          const {
            start: _s,
            end: _e,
            ...safe
          } = data;
          results.push({
            ...safe,
            // spread first so computed fields win
            id: docSnap.id,
            originalId: docSnap.id,
            originalEventId: docSnap.id,
            occurrenceId: null,
            title,
            start,
            end,
            type: 'oneOff',
            mode,
            onlineUrl,
            location: data.location || {}
          });
        } else if (isRecurring && data.recurrence?.byDay?.length && data.recurrence.time) {
          const {
            byDay
          } = data.recurrence;
          const dayCodes = Array.from(new Set(byDay.filter(code => ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].includes(code))));
          if (!dayCodes.length) return;
          const today = new Date();
          const maxOccurrences = getWeeklyRecurrenceScanLimit({
            now: today,
            recurrenceEndDate: data.recurrence?.endDate,
            fallbackOccurrences: 24
          });
          dayCodes.forEach(dayCode => {
            const occurrenceDates = getRecurrenceDatesForDay({
              now: today,
              dayCode,
              recurrence: { ...data.recurrence, frequency: data.recurrence.frequency || data.frequency || 'WEEKLY', weekOfMonth: data.recurrence.weekOfMonth || data.weekOfMonth },
              maxOccurrences
            });
            occurrenceDates.forEach((d, i) => {
              const end = new Date(d.getTime() + duration);
              const {
                start: _s,
                end: _e,
                ...safe
              } = data;
              const occurrenceId = toOccurrenceId(d);
              results.push({
                ...safe,
                id: occurrenceId ? `${docSnap.id}__${occurrenceId}` : `${docSnap.id}-${dayCode}-${i}`,
                originalId: docSnap.id,
                originalEventId: docSnap.id,
                occurrenceId,
                title,
                start: new Date(d),
                end,
                type: 'recurring',
                mode,
                onlineUrl,
                location: data.location || {}
              });
            });
          });
        }
      });

      // Remove generated recurring instances where an occurrence override exists.
      const filtered = results.filter(entry => {
        if (!entry.occurrenceId || !entry.originalId) return true;
        const key = `${entry.originalId}__${entry.occurrenceId}`;
        return !occurrenceOverrides.has(key);
      });
      occurrenceOverrides.forEach(entry => {
        if (String(entry.status || '').toLowerCase() === 'cancelled') return;
        filtered.push(entry);
      });
      if (!cancelled) {
        setEvents(filtered);
        setEventsError('');
      }
    };
    fetchEvents().catch(() => {
      if (cancelled) return;
      setEvents([]);
      setEventsError('Could not load calendar events. Please refresh and try again.');
    });
    return () => {
      cancelled = true;
    };
  }, [authzChecked, isAdmin, teamId, refreshKey]);

  // Merge user mirror RSVP into events (colour pills driven by userEventMap)
  const annotatedEvents = useMemo(() => {
    if (!events.length) return events;
    if (!userId) return events;
    return events.map(ev => {
      const mirror = userEventMap[ev.originalId];
      const occurrenceMirror = ev.occurrenceId ? mirror?.occurrences?.[ev.occurrenceId] : null;
      // Compatibility for one-off RSVPs written with occurrence scope by the
      // waitlist rollout client. New writes use series/event scope.
      const legacyOneOffMirror = !ev.occurrenceId && ev.date ? mirror?.occurrences?.[ev.date] : null;
      const seriesMirror = mirror?.series || null;
      const resolvedMirror = occurrenceMirror || legacyOneOffMirror || (!ev.occurrenceId || rsvpSeriesResponsesEnabled ? seriesMirror : null);
      const ticketMirror = userTicketMap[ev.originalId];
      const hasPaidTicket = Boolean(
        ticketMirror?.series || (ev.occurrenceId && ticketMirror?.occurrences?.[ev.occurrenceId])
      );
      return {
        ...ev,
        _myRsvp: resolvedMirror?.rsvpStatus ?? null,
        _myTicket: hasPaidTicket ? 'paid' : null,
        // 'yes' | 'no' | null
        _needsResponse: !!resolvedMirror?.needsResponse // boolean
      };
    });
  }, [events, rsvpSeriesResponsesEnabled, userEventMap, userId, userTicketMap]);
  const monthEventCountByDate = useMemo(() => {
    const counts = {};
    annotatedEvents.forEach(event => {
      const key = formatDateInputValue(event.start);
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [annotatedEvents]);
  const getFirstEventStartForDate = useCallback(targetDate => {
    const targetKey = formatDateInputValue(targetDate);
    if (!targetKey) return null;
    let earliest = null;
    annotatedEvents.forEach(event => {
      if (formatDateInputValue(event.start) !== targetKey) return;
      const start = new Date(event.start);
      if (Number.isNaN(start.getTime())) return;
      if (!earliest || start < earliest) earliest = start;
    });
    return earliest;
  }, [annotatedEvents]);
  const getSingleAgendaEventForDate = useCallback(targetDate => {
    const targetKey = formatDateInputValue(targetDate);
    if (!targetKey) return null;
    const matches = annotatedEvents.filter(event => formatDateInputValue(event.start) === targetKey);
    return matches.length === 1 ? matches[0] : null;
  }, [annotatedEvents]);
  const openCalendarEvent = useCallback(event => {
    if (!event) return;
    setSelectedEvent({
      ...event,
      id: event.originalId,
      eventMode: event.mode,
      onlineUrl: event.onlineUrl,
      readOnly: !isAdmin
    });
    setModalOpen(true);
  }, [isAdmin]);
  const copyCalendarEvent = useCallback(event => {
    setSelectedEvent(buildEventCopyInitialData(event));
    setModalOpen(true);
  }, []);

  // Background/border + a CSS var for title colour
  const eventPropGetter = useMemo(() => {
    return event => {
      const isMonthView = calendarView === 'month';
      const isCompactMonth = isMonthView && isMobile;
      const style = {
        borderRadius: isMonthView ? isCompactMonth ? 6 : 8 : 6,
        borderWidth: 1,
        borderStyle: 'solid',
        padding: isMonthView ? isCompactMonth ? 2 : 6 : 4,
        '--evtTitle': '#0f172a' // fallback very dark slate
      };
      if (event.attendanceMode === 'ticketed') {
        if (event._myTicket === 'paid') {
          style.backgroundColor = '#D1FAE5';
          style.borderColor = '#34D399';
          style['--evtTitle'] = '#065F46';
        } else {
          style.backgroundColor = '#EDE9FE';
          style.borderColor = '#C4B5FD';
          style['--evtTitle'] = '#3B0764';
        }
      } else if (event.attendanceMode === 'rsvp') {
        if (event._myRsvp === 'yes') {
          style.backgroundColor = '#D1FAE5';
          style.borderColor = '#34D399';
          style['--evtTitle'] = '#065F46';
        } else if (event._myRsvp === 'no') {
          style.backgroundColor = '#E5E7EB';
          style.borderColor = '#9CA3AF';
          style['--evtTitle'] = '#111827';
        } else {
          style.backgroundColor = '#BFDBFE';
          style.borderColor = '#60A5FA';
          style['--evtTitle'] = '#1E3A8A';
        }
      } else {
        // non-trackable events
        style.backgroundColor = '#F1F5F9'; // slate-100
        style.borderColor = '#CBD5E1'; // slate-300
        style['--evtTitle'] = '#0F172A'; // slate-900
      }
      return {
        style
      };
    };
  }, [calendarView, isMobile]);
  const calendarSelectable = shouldEnableTeamCalendarSelection({
    calendarView,
    isMobile,
    isAdmin
  });

  // If we have an ?event= query param, auto-open it once events load
  useEffect(() => {
    if (!pendingQueryEvent || !events.length) return;
    const target = pendingQueryOccurrence ? events.find(ev => ev.originalId === pendingQueryEvent && ev.occurrenceId === pendingQueryOccurrence) || events.find(ev => ev.id === `${pendingQueryEvent}__${pendingQueryOccurrence}`) : events.find(ev => ev.originalId === pendingQueryEvent) || events.find(ev => ev.id === pendingQueryEvent);
    if (target) {
      setSelectedEvent({
        ...target,
        id: target.originalId,
        eventMode: target.mode,
        onlineUrl: target.onlineUrl,
        readOnly: !isAdmin,
        focusComments: pendingQueryComments
      });
      setModalOpen(true);
      setPendingQueryEvent(null);
      setPendingQueryOccurrence(null);
      setPendingQueryComments(false);
    }
  }, [pendingQueryComments, pendingQueryEvent, pendingQueryOccurrence, events, isAdmin]);

  // Custom event renderer (title uses --evtTitle)
  const CalendarEvent = ({
    event
  }) => {
    const pills = [];
    const startTime = event.start ? new Date(event.start).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    }) : null;

    // Mode pills
    if (event.mode === 'inPerson') {
      pills.push({
        label: 'In person',
        bg: '#e0e7ff',
        color: '#1e3a8a',
        border: '#c7d2fe'
      });
    } else if (event.mode === 'online' || event.onlineUrl) {
      pills.push({
        label: 'Online',
        bg: '#cffafe',
        color: '#0e7490',
        border: '#06b6d4'
      });
    }

    // Attendance pills
    if (event.attendanceMode === 'ticketed') {
      pills.push({
        label: 'Ticketed',
        bg: '#ede9fe',
        color: '#581c87',
        border: '#c4b5fd'
      });
      if (event._myTicket === 'paid') {
        pills.push({
          label: 'Booked',
          bg: '#dcfce7',
          color: '#166534',
          border: '#86efac'
        });
      }
    } else if (event.attendanceMode === 'rsvp') {
      pills.push({
        label: 'RSVP',
        bg: '#dbeafe',
        color: '#1e40af',
        border: '#93c5fd'
      });
      if (event._needsResponse && event._myRsvp == null) {
        pills.push({
          label: 'Respond',
          bg: '#ffedd5',
          color: '#9a3412',
          border: '#fdba74'
        });
      }
      if (event._myRsvp === 'yes') {
        pills.push({
          label: 'Going',
          bg: '#dcfce7',
          color: '#166534',
          border: '#86efac'
        });
      } else if (event._myRsvp === 'no') {
        pills.push({
          label: 'Not going',
          bg: '#e5e7eb',
          color: '#111827',
          border: '#9ca3af'
        });
      }
    }
    return <div style={{
      display: 'grid',
      gap: 2
    }}>
        {startTime && <div style={{
        fontSize: 10,
        fontWeight: 600,
        color: '#0f172a',
        background: '#fff',
        border: '1px solid #cbd5e1',
        borderRadius: 6,
        padding: '1px 5px',
        width: 'fit-content',
        lineHeight: 1.2
      }}>
            {startTime}
          </div>}
        <div style={{
        fontWeight: 700,
        lineHeight: 1.1,
        fontSize: 12,
        color: 'var(--evtTitle, #0f172a)'
      }}>
          {event.title}
        </div>
        <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4
      }}>
          {pills.map((p, i) => <span key={i} style={{
          fontSize: 10,
          padding: '1px 5px',
          borderRadius: 999,
          background: p.bg,
          color: p.color,
          border: `1px solid ${p.border}`,
          whiteSpace: 'nowrap'
        }}>
              {p.label}
            </span>)}
        </div>
      </div>;
  };
  const CalendarEventCompact = ({
    event
  }) => {
    const startTime = event.start ? new Date(event.start).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    }) : null;
    return <div style={{
      fontWeight: 700,
      lineHeight: 1.2,
      fontSize: 12,
      color: 'var(--evtTitle, #0f172a)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }}>
        {startTime ? `${startTime} ` : ''}{event.title}
      </div>;
  };
  const AgendaEvent = ({
    event,
    title
  }) => <div className="team-calendar-agenda-event">
      {title || event.title}
    </div>;
  const AgendaDate = ({
    day,
    label
  }) => {
    const event = getSingleAgendaEventForDate(day);
    if (!event) return <span>{label}</span>;
    return <button type="button" className="team-calendar-agenda-cell-button" onClick={e => {
      e.stopPropagation();
      openCalendarEvent(event);
    }}>
        {label}
      </button>;
  };
  const AgendaTime = ({
    event,
    label
  }) => <button type="button" className="team-calendar-agenda-cell-button" onClick={e => {
    e.stopPropagation();
    openCalendarEvent(event);
  }}>
      {label}
    </button>;
  const MonthDateHeader = ({
    date,
    label
  }) => {
    const dayKey = formatDateInputValue(date);
    const eventCount = monthEventCountByDate[dayKey] || 0;
    const hasMultipleEvents = eventCount > 1;
    const shouldOpenDayFromHeader = calendarView === 'month' && (isMobile || hasMultipleEvents);
    const openDayFromHeader = e => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      const nextDate = new Date(date);
      if (Number.isNaN(nextDate.getTime())) return;
      setScrollToTime(buildScrollToTime(getFirstEventStartForDate(nextDate)));
      setCalendarDate(nextDate);
      setCalendarView('day');
    };
    return <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6
    }} role={shouldOpenDayFromHeader ? 'button' : undefined} tabIndex={shouldOpenDayFromHeader ? 0 : undefined} onMouseDown={shouldOpenDayFromHeader ? e => {
      e.preventDefault();
      e.stopPropagation();
    } : undefined} onClick={shouldOpenDayFromHeader ? openDayFromHeader : undefined} onKeyDown={shouldOpenDayFromHeader ? e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDayFromHeader();
      }
    } : undefined}>
        <span>{label}</span>
        {hasMultipleEvents && <span title={`${eventCount} events on this day. Scroll within the week row to view more.`} style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        color: '#0f172a',
        background: '#e2e8f0',
        border: '1px solid #cbd5e1',
        lineHeight: 1
      }}>
            {eventCount > 9 ? '9+' : eventCount}
          </span>}
      </div>;
  };
  const legendItems = [{
    label: 'Awaiting RSVP',
    background: '#BFDBFE',
    border: '#60A5FA',
    color: '#1E3A8A'
  }, {
    label: 'Going',
    background: '#D1FAE5',
    border: '#34D399',
    color: '#065F46'
  }, {
    label: 'Not going',
    background: '#E5E7EB',
    border: '#9CA3AF',
    color: '#111827'
  }, {
    label: 'Ticketed',
    background: '#EDE9FE',
    border: '#C4B5FD',
    color: '#3B0764'
  }, {
    label: 'Info only',
    background: '#F1F5F9',
    border: '#CBD5E1',
    color: '#0F172A'
  }];
  if (authLoading) {
    return <div className="calendar-page">
        <p className="text-muted">Loading…</p>
      </div>;
  }
  if (!teamId || !userId) {
    return <div className="calendar-page">
        <p className="text-muted">Unable to load calendar. Please access this page from the team page.</p>
        <button onClick={() => navigate(teamId ? `/teams/${teamId}` : '/teams')} className="button button-secondary mt-4">
          Back to Team
        </button>
      </div>;
  }
  if (!authzChecked) {
    return <div className="calendar-page">
        <p className="text-muted">Checking club access…</p>
      </div>;
  }
  return <div className="calendar-page">
      <div className="calendar-header-row" style={{
      marginBottom: 16
    }}>
        {isMobile ? <>
            <div style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}>
              <h2 style={{
            margin: 0,
            flex: 1
          }}>{teamName} Calendar</h2>
              <button onClick={() => navigate(`/teams/${teamId}`)} className="button button-secondary" style={compactActionStyle}>
                Back to Team
              </button>
            </div>
            {isAdmin && <div style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          marginTop: 8
        }}>
                <button className="button button-secondary" style={compactActionStyle} onClick={() => openFreeEventModal()}>
                  New Free Event
                </button>

                <button className="button button-primary" style={compactActionStyle} onClick={openPaidEventModal}>
                  New Paid Event
                </button>
              </div>}
          </> : <div style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
            <h2 style={{
          margin: 0
        }}>{teamName} Calendar</h2>
            <button onClick={() => navigate(`/teams/${teamId}`)} className="button button-secondary" style={compactActionStyle}>
              Back to Team
            </button>

            {isAdmin && <div style={{
          display: 'flex',
          gap: 8,
          marginLeft: 'auto'
        }}>
                <button className="button button-secondary" style={compactActionStyle} onClick={() => openFreeEventModal()}>
                  New Free Event
                </button>

                <button className="button button-primary" style={compactActionStyle} onClick={openPaidEventModal}>
                  New Paid Event
                </button>
              </div>}
          </div>}
      </div>

      <div style={{
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      marginBottom: 12
    }}>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        minWidth: 0,
        flex: '1 1 420px'
      }}>
          <span style={{
          fontSize: 13,
          fontWeight: 700,
          color: '#0f172a'
        }}>Colours:</span>
          <span style={{
          fontSize: 12,
          color: '#475569'
        }}>response status, not tags</span>
          {legendItems.map(item => <span key={item.label} style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '0.22rem 0.45rem',
          borderRadius: 999,
          background: '#fff',
          border: '1px solid #e2e8f0',
          color: '#0f172a',
          fontSize: 12,
          lineHeight: 1.2
        }}>
              <span aria-hidden="true" style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: item.background,
            border: `1px solid ${item.border}`,
            boxSizing: 'border-box',
            flex: '0 0 auto'
          }} />
              <span>
                <span style={{
              fontWeight: 600,
              color: item.color
            }}>{item.label}</span>
              </span>
            </span>)}
        </div>
        <div style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
          <label htmlFor="calendar-date-jump" style={{
          fontSize: 14,
          fontWeight: 600,
          color: '#0f172a'
        }}>
            Jump to date
          </label>
          <DateJumpInput id="calendar-date-jump" date={calendarDate} onDateChange={setCalendarDate} />
          <button className="button button-secondary" onClick={() => setCalendarDate(new Date())} style={compactActionStyle}>
            Today
          </button>
        </div>
      </div>

      {eventsError && <div role="alert" className="form-error" style={{
      marginBottom: 12
    }}>
          {eventsError}
        </div>}

      <div ref={calendarShellRef}>
        <Calendar localizer={localizer} events={annotatedEvents} startAccessor="start" endAccessor="end" style={{
        height: calendarHeight
      }} defaultView="month" view={calendarView} onView={nextView => setCalendarView(nextView)} date={calendarDate} onNavigate={nextDate => setCalendarDate(nextDate)} scrollToTime={scrollToTime} popup showAllEvents={false} allDayMaxRows={calendarView === 'month' && isMobile ? 1 : 2} dayLayoutAlgorithm="no-overlap" selectable={calendarSelectable} eventPropGetter={eventPropGetter} components={{
        event: calendarView === 'month' && !isMobile ? CalendarEvent : CalendarEventCompact,
        agenda: {
          date: AgendaDate,
          event: AgendaEvent,
          time: AgendaTime
        },
        month: {
          dateHeader: MonthDateHeader
        }
      }} onSelectEvent={openCalendarEvent} onSelectSlot={slot => {
        if (calendarView === 'month' && isMobile) {
          const nextDate = new Date(slot.start);
          if (Number.isNaN(nextDate.getTime())) return;
          setCalendarDate(nextDate);
          setCalendarView('day');
          return;
        }
        if (!isAdmin) return;
        openFreeEventModal(slot.start);
      }} />
      </div>

      {modalOpen && selectedEvent && <EventFormModal key={selectedEvent._copyToken || selectedEvent.id || selectedEvent.originalId || 'new-event'} teamId={teamId} isOpen={true} onClose={() => {
      setModalOpen(false);
      setSelectedEvent(null);
      setRefreshKey(k => k + 1);
    }} onCopy={copyCalendarEvent} initialData={selectedEvent} readOnly={selectedEvent.readOnly} userId={userId} isAdmin={isAdmin} canCheckIn={canCheckIn} canModerateEventComments={canModerateEventComments} canManageEventRefunds={canManageEventRefunds} teamBrand={teamBrand} focusComments={Boolean(selectedEvent.focusComments)} calendarSurface="team" />}
      {stripeGuardDialog}
    </div>;
}
